import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBookingFlowStore, SelectedStaff } from '../../stores/useBookingFlowStore';
import { useStaffStore } from '../../stores/useStaffStore';
import ProgressBar from '../../components/booking/ProgressBar';
import StaffCard from '../../components/booking/StaffCard';
import { toast } from 'react-hot-toast';

const StaffSelectionPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { service, dateTime, staff: selectedStaff, setStaff, canProceedToStaff } = useBookingFlowStore();
  const { staff: staffList, isLoading, fetchStaff } = useStaffStore();

  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(selectedStaff?.id || null);

  // 驗證是否可以進入此頁面
  useEffect(() => {
    if (!canProceedToStaff()) {
      navigate(`/book/${storeId}`);
    }
  }, [canProceedToStaff, navigate, storeId]);

  // 載入員工列表（使用 zustand store 帶快取）
  useEffect(() => {
    if (storeId) {
      fetchStaff(storeId);
    }
  }, [storeId, fetchStaff]);

  const handleStaffSelect = (staff: any) => {
    setSelectedStaffId(staff.id);
  };

  const handleSkip = () => {
    setStaff(null);
    navigate(`/book/${storeId}/info`);
  };

  const handleNext = () => {
    if (!selectedStaffId) {
      toast.error(t('pages:booking.staffSelection.selectStaffError'));
      return;
    }

    const staff = staffList.find((s) => s.id === selectedStaffId);
    if (!staff) return;

    const staffData: SelectedStaff = {
      id: staff.id,
      name: staff.name,
      phone: staff.phone,
      email: staff.email,
    };

    setStaff(staffData);
    navigate(`/book/${storeId}/info`);
  };

  if (!service || !dateTime) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* 進度條 */}
        <ProgressBar
          currentStep={3}
          totalSteps={4}
          steps={['選擇服務', '選擇時間', '填寫資訊', '完成預約']}
        />

        {/* 返回按鈕 */}
        <button
          onClick={() => navigate(`/book/${storeId}/datetime`)}
          className="mb-6 flex items-center space-x-2 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('pages:booking.staffSelection.backToDateTime')}</span>
        </button>

        {/* 頁面標題 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('pages:booking.staffSelection.title')}
          </h1>
          <div className="text-gray-600 dark:text-gray-400 space-y-1">
            <p>{t('pages:booking.staffSelection.service')}<span className="font-semibold">{service.name}</span></p>
            <p>{t('pages:booking.staffSelection.time')}<span className="font-semibold">{dateTime.date} {dateTime.displayTime}</span></p>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-12">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            </div>
          </div>
        ) : (
          <>
            {/* 不指定員工選項 */}
            <div className="mb-6">
              <button
                onClick={handleSkip}
                className="w-full bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 text-left hover:shadow-lg transition-all duration-200 border-2 border-dashed border-gray-300 dark:border-slate-600 hover:border-brand-400 dark:hover:border-brand-600"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                      {t('pages:booking.staffSelection.noStaff')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('pages:booking.staffSelection.noStaffSubtitle')}
                    </p>
                  </div>
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>

            {/* 員工列表 */}
            {staffList.length > 0 ? (
              <>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  {t('pages:booking.staffSelection.orSelectStaff')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {staffList.map((staff) => (
                    <StaffCard
                      key={staff.id}
                      id={staff.id}
                      name={staff.name}
                      phone={staff.phone}
                      email={staff.email}
                      isSelected={selectedStaffId === staff.id}
                      onClick={() => handleStaffSelect(staff)}
                    />
                  ))}
                </div>

                {/* 下一步按鈕 */}
                {selectedStaffId && (
                  <div className="flex justify-end">
                    <button
                      onClick={handleNext}
                      className="px-8 py-3 rounded-lg font-bold text-white bg-brand-600 hover:bg-brand-700 shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      {t('pages:booking.staffSelection.nextStep')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-12 text-center">
                <div className="text-4xl mb-4">👤</div>
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  {t('pages:booking.staffSelection.noAvailableStaff')}
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                  {t('pages:booking.staffSelection.continueWithoutStaff')}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StaffSelectionPage;
